import { Injectable } from '@angular/core';
import { FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { BehaviorSubject } from 'rxjs';

export enum PresetDateFilters {
  CURRENT_MONTH = 'Current Month',
  ONE_MONTH = '1 Month',
  THREE_MONTHS = '3 Months ',
  ONE_YEAR = '1 Year ',
  YTD = 'YTD',
  CUSTOM = 'Custom',
}

export enum DashboardFilterFields {
  CREATED = 'created',
  ENVIRONMENTS = 'environments',
  ASSIGNEE = 'assignee',
  AUTHOR = 'author',
  STATUS = 'status',
  PRIORITY = 'priority',
  OUTCOME = 'outcome',
}

export enum DashboardChartExports {
  FILTERS = 'Filters',
  OVERVIEW = 'Overview',
  COVERAGE = 'Coverage',
  MITRE_HEATMAP = 'MITRE Heatmap',
  METRICS = 'Metrics',
  WORKLOAD_DISTRIBUTION = 'Workload Distribution',
}

export interface DashboardState {
  filter: {
    filters: FilterDescriptor[];
    logic: 'and' | 'or';
  };
  archived: boolean;
  presetDateFilter: PresetDateFilters;
}

// Set initial state to current month
export const DEFAULT_STATE: DashboardState = {
  filter: {
    filters: [
      {
        field: DashboardFilterFields.CREATED,
        operator: FilterOperator.GreaterThanOrEqual,
        value: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      },
    ],
    logic: 'and',
  },
  archived: false,
  presetDateFilter: PresetDateFilters.CURRENT_MONTH,
};

@Injectable()
export class DashboardStateService {
  /**
   * The complete current state of the dashboard
   */
  public dashboardState: BehaviorSubject<DashboardState> = new BehaviorSubject({
    ...DEFAULT_STATE,
  });

  /**
   * Sets the entire state to the passed object, defaulting any unprovided values back to app defaults.
   * Will only update external state if there are changes between the passed state and the current dashboard state.
   *
   * @param state
   */
  public setState(state: Partial<DashboardState>): void {
    this.updateState({ ...DEFAULT_STATE, ...state });
  }

  /**
   * Returns the dashboard state
   */
  public getState(): DashboardState {
    return this.dashboardState.value;
  }

  /**
   * Reset the dashboard state
   */
  public reset(): void {
    this.updateState(DEFAULT_STATE);
  }

  public updateState(newState?: Partial<DashboardState>): void {
    this.dashboardState.next({
      ...this.dashboardState.value,
      ...newState,
    });
  }
}
